import styled, { css } from 'styled-components';
import { keyframes } from 'styled-components';

import { staticColors } from '~/common/styles/theme';
import { SPACING } from '~/common/layout-propertis/spacing';
import { RADIUS } from '~/common/layout-propertis/radius';
import { Z_INDEX } from '~/common/constants/z-index';
import { TAlertToastPosition } from '~/common/@types/AlertToastPosition';

const bounceKeyframes = {
  'top-left': keyframes`
    0% { transform: translateX(-100%); opacity: 0; }
    80% { transform: translateX(10px); opacity: 1; }
    100% { transform: translateX(0); }
  `,
  top: keyframes`
    0% { transform: translate(-50%, -100%); opacity: 0; }
    80% { transform: translate(-50%, 10px); opacity: 1; }
    100% { transform: translate(-50%, 0); }
  `,
  'top-right': keyframes`
    0% { transform: translateX(100%); opacity: 0; }
    80% { transform: translateX(-10px); opacity: 1; }
    100% { transform: translateX(0); }
  `,
  'bottom-left': keyframes`
    0% { transform: translateX(-100%); opacity: 0; }
    80% { transform: translateX(10px); opacity: 1; }
    100% { transform: translateX(0); }
  `,
  bottom: keyframes`
    0% { transform: translate(-50%, 100%); opacity: 0; }
    80% { transform: translate(-50%, -10px); opacity: 1; }
    100% { transform: translate(-50%, 0); }
  `,
  'bottom-right': keyframes`
    0% { transform: translateX(100%); opacity: 0; }
    80% { transform: translateX(-10px); opacity: 1; }
    100% { transform: translateX(0); }
  `,
};

const fadeOutKeyframes = {
  'top-left': keyframes`
    0% { transform: translateX(0); opacity: 1; }
    100% { transform: translateX(-100%) scale(0.9); opacity: 0; }
  `,
  top: keyframes`
    0% { transform: translate(-50%, 0); opacity: 1; }
    100% { transform: translate(-50%, -100%) scale(0.9); opacity: 0; }
  `,
  'top-right': keyframes`
    0% { transform: translateX(0); opacity: 1; }
    100% { transform: translateX(100%) scale(0.9); opacity: 0; }
  `,
  'bottom-left': keyframes`
    0% { transform: translateX(0); opacity: 1; }
    100% { transform: translateX(-100%) scale(0.9); opacity: 0; }
  `,
  bottom: keyframes`
    0% { transform: translate(-50%, 0); opacity: 1; }
    100% { transform: translate(-50%, 100%) scale(0.9); opacity: 0; }
  `,
  'bottom-right': keyframes`
    0% { transform: translateX(0); opacity: 1; }
    100% { transform: translateX(100%) scale(0.9); opacity: 0; }
  `,
};

const positionStyles = {
  'top-left': css`
    top: 20px;
    left: 20px;
  `,
  top: css`
    top: 20px;
    left: 50%;
    transform: translateX(-50%);
  `,
  'top-right': css`
    top: 20px;
    right: 20px;
  `,
  'bottom-left': css`
    bottom: 20px;
    left: 20px;
  `,
  bottom: css`
    bottom: 20px;
    left: 50%;
    transform: translateX(-50%);
  `,
  'bottom-right': css`
    bottom: 20px;
    right: 20px;
  `,
};
interface IToastContainerProps {
  position: TAlertToastPosition;
  visible: boolean;
  backgroundColor: string;
  asBanner: boolean;
}
export const ToastContainer = styled.div<IToastContainerProps>`
  position: fixed;
  opacity: 0;
  pointer-events: none;
  height: min-content;
  width: ${({ asBanner }) => (asBanner ? '98%' : '500px')};
  min-height: ${({ asBanner }) => (asBanner ? '36px' : '44px')};
  background-color: ${({ backgroundColor }) => backgroundColor};
  padding: ${SPACING._8};
  border-radius: ${RADIUS._4};
  display: flex;
  flex-direction: column;
  gap: ${SPACING._8};
  justify-content: center;
  z-index: ${Z_INDEX.ALERT_TOAST};

  @media (max-width: 750px) {
    width: 98%;
  }

  transition: opacity 0.4s ease;
  ${({ position }) => positionStyles[position]};
  ${({ visible, position }) =>
    visible
      ? css`
          opacity: 1;
          pointer-events: auto;
          animation: ${bounceKeyframes[position]} 200ms ease-out;
        `
      : css`
          animation: ${fadeOutKeyframes[position]} 200ms ease-in forwards;
        `}
`;

export const DescriptionWrapper = styled.div`
  padding-left: calc(8px + 20px);
  padding-right: 40px;
  width: 100%;
  span {
    word-break: normal;
    color: ${staticColors.grey[0]};
  }
`;

interface IToastMainInfoRowProps {
  asBanner: boolean;
}
export const ToastMainInfoRow = styled.div<IToastMainInfoRowProps>`
  display: flex;
  flex-direction: row;
  justify-content: ${({ asBanner }) => (asBanner ? 'center' : 'start')};
  gap: ${SPACING._8};
  align-items: center;
  position: relative;
  padding-right: ${SPACING._32};

  span {
    color: ${staticColors.grey[0]};
  }

  #right {
    margin-left: ${({ asBanner }) => (asBanner ? 'unset' : 'auto')};
    display: flex;
    flex-direction: row;
    justify-content: start;
    gap: ${SPACING._8};
    align-items: center;
  }
`;

export const CloseWrapper = styled.div`
  position: absolute;
  right: 0;
  display: flex;
  align-items: center;
`;

export const BottomInfoContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${SPACING._8};
  padding-left: calc(8px + 20px);
`;
